/**
 * Widget V2 - Bottom Navigation
 *
 * Mobile-only persistent nav. Home is the front door (discovery lives there);
 * Bookings is the returning-user surface; Account is profile/auth.
 */

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Paper from "@mui/material/Paper";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import HomeIcon from "@mui/icons-material/Home";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";

const ACCOUNT_ROUTES = new Set(["login", "register", "claim", "email-signup", "profile"]);
const HOME_DISCOVERY_ROUTES = new Set(["coaches", "locations", "services"]);

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useWidget();
  const { isAuthenticated } = useAuth();

  // Path within the widget (everything after /:slug/).
  const currentPath = location.pathname.split("/").slice(2).join("/") || "";
  const firstSeg = currentPath.split("/")[0];
  const dashTab = new URLSearchParams(location.search).get("tab");

  const getActiveValue = () => {
    if (currentPath === "" || currentPath === "home") return 0;
    if (HOME_DISCOVERY_ROUTES.has(firstSeg)) return 0;
    // Bookings and Account share the dashboard route; the ?tab tells them apart.
    if (firstSeg === "dashboard" || firstSeg === "activity") return dashTab === "bookings" ? 1 : 2;
    if (ACCOUNT_ROUTES.has(firstSeg)) return 2;
    return -1;
  };

  const goto = (path) => navigate(`/${slug}/${path}`);

  const handleChange = (_event, newValue) => {
    switch (newValue) {
      case 0:
        return goto("");
      case 1:
        // Bookings deep-links into the dashboard's Bookings tab; bounce to login if needed.
        return isAuthenticated ? goto("dashboard?tab=bookings") : goto("login?redirect=dashboard");
      case 2:
        return isAuthenticated ? goto("dashboard") : goto("login");
      default:
        return goto("");
    }
  };

  return (
    <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000, borderTop: 1, borderColor: "divider" }} elevation={3}>
      <BottomNavigation value={getActiveValue()} onChange={handleChange} showLabels>
        <BottomNavigationAction label="Home" icon={<HomeIcon />} />
        <BottomNavigationAction label="Bookings" icon={<CalendarMonthIcon />} />
        <BottomNavigationAction label="Account" icon={<PersonIcon />} />
      </BottomNavigation>
    </Paper>
  );
}

export default BottomNav;
